/*
 * UserProfile form
 * Edit your profile
 */

import * as React from 'react';

import { Button, Col, Row } from 'react-bootstrap';
import { AbstractControl, FieldConfig, FormGenerator, FormGroup, Observable } from 'react-reactive-form';
import { FormUtil, userBaseConfigControls } from '../common/FormUtil';

import { TFunction } from 'react-i18next';
import { WithTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { constants } from '../../constants/constants';
import { langOption } from '../../i18n';
import { Ioption, Iuser } from '../../models';
import { userLanguageEnum, userLanguageLabels, userLanguageOptions } from '../../models-enums';

interface IstateChanges extends Observable<any> {
    next: () => void;
}

interface AbstractControlEdited extends AbstractControl {
    stateChanges: IstateChanges;
}

const buildFieldConfig = (facilityOptions: any[], user: Iuser, codes: any, t: TFunction) => {
    const languageOptions = userLanguageOptions;
    const standardCodes: Ioption[] = codes;
    const userCodes = standardCodes.filter(code => user.userStandards.includes(code.value));
    
    const dashboardSortOptions = [
        { label: t('manageWorkOrder:status'), value: 'status' },
        { label: t('manageWorkOrder:jobNumberTableHeader'), value: 'jobNumber' },
        { label: t('manageInventory:sapWoNum'), value: 'workOrderNumber' },
        { label: t('nsJob:facility'), value: 'facilityName' },
        { label: t('manageWorkOrder:description'), value: 'description' },
        { label: t('user:city'), value: 'city' },
        { label: t('nsJob:startDate'), value: 'startDate' },
        { label: t('nsJob:orderType'), value: 'orderType' },
        { label: t('dashboardAnalytics:Hours'), value: 'hours' },
        { label: t('manageWorkOrder:labelParts'), value: 'partStatus' }
    ];
    
    const defaultDashboardSort = user.defaultDashboardSort || 'startDate';
    const defaultDashboardSortOption = 
        dashboardSortOptions.find(x => x.value === defaultDashboardSort) || 
        dashboardSortOptions.find(x => x.value === 'startDate');

    // Field config to configure form
    const fieldConfig = {
        controls: {
            ...userBaseConfigControls,
            language: {
                render: FormUtil.Select,
                meta: {
                    options: languageOptions,
                    label: 'common:language',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    name: 'language'
                },
                options: {
                    validators: FormUtil.validators.requiredWithTrim
                }
            },
            defaultDashboardSort: {
                render: FormUtil.Select,
                meta: {
                    options: dashboardSortOptions,
                    label: 'user:defaultDashboardSort',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    name: 'defaultDashboardSort',
                    helpText: 'user:defaultDashboardSort_help'
                },
                formState: {
                    value: defaultDashboardSortOption,
                    disabled: false
                }
            },
            standards: {
                render: FormUtil.Select,
                meta: {
                    options: standardCodes,
                    label: 'common:standards',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: true,
                    name: 'standards'
                },
                formState: {
                    value: userCodes,
                    disabled: false
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                }
            }
        }
    };

    return fieldConfig as FieldConfig;
};

interface Iprops {
    colorButton: string;
    handleSubmit: any;
    loading: boolean;
    t: TFunction;
    toggleModal: () => void;
    user: Iuser;
    standardCodes: Ioption[];
}

class UserProfileForm extends React.Component<Iprops & WithTranslation, { userValuesUpdated: boolean }> {
    private formGroup: FormGroup | any;
    private fieldConfig: FieldConfig;
    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.fieldConfig = FormUtil.translateForm(
            buildFieldConfig([], this.props.user, this.props.standardCodes, this.props.t),
            this.props.t
        );
        this.state = {
            userValuesUpdated: false
        };
    }

    componentDidMount() {
        this.updateFieldConfig();
    }

    componentDidUpdate(prevProps: Iprops & WithTranslation): void {
        // Check if user data or standardCodes have changed
        if (prevProps.user !== this.props.user || prevProps.standardCodes !== this.props.standardCodes) {
            this.updateFieldConfig();
            this.setState({ userValuesUpdated: false }, () => {
                this.populateFormWithUserData();
            });
        } else if (!this.state.userValuesUpdated && this.formGroup) {
            this.populateFormWithUserData();
        }
    }

    updateFieldConfig = () => {
        this.fieldConfig = FormUtil.translateForm(
            buildFieldConfig([], this.props.user, this.props.standardCodes, this.props.t),
            this.props.t
        );
    };

    populateFormWithUserData = () => {
        if (!this.formGroup || !this.props.user) return;

        this.setState({ userValuesUpdated: true }, () => {
            // Populate basic user fields
            const userData = {
                first: this.props.user.first || '',
                last: this.props.user.last || '',
                email: this.props.user.email || '',
                phone: this.props.user.phone || '',
                position: this.props.user.position || ''
            };

            this.formGroup.patchValue(userData);

            // Handle email control
            const emailControl = this.formGroup.get('email') as AbstractControlEdited;
            if (emailControl) {
                emailControl.disable();
            }

            // Handle language field
            let userLang = {
                value: 0,
                label: 'English'
            };

            const { language } = this.props.user;
            if (language && userLanguageEnum[language]) {
                userLang = {
                    value: language,
                    label: userLanguageLabels[language as userLanguageEnum] ?? 'English'
                };
            }

            FormUtil.patchControl(this.formGroup, 'language', userLang);
        });
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }

        // Only change language if it actually changed
        const newLanguageValue = this.formGroup.value.language.value;
        const currentLanguageValue = this.props.user.language;
        
        if (newLanguageValue !== currentLanguageValue) {
            const lang = langOption(newLanguageValue);
            
            this.props.i18n
                .changeLanguage(lang)
                .then(() => {
                    toastr.success(
                        'Success',
                        `Language changed to ${this.formGroup.value.language.label}.`,
                        constants.toastrSuccess
                    );
                })
                .catch(() => {
                    toastr.error(
                        'Error',
                        `Failed to update language to ${this.formGroup.value.language.label}.`,
                        constants.toastrError
                    );
                });
        }

        // Extract defaultDashboardSort value
        const defaultDashboardSort = 
            typeof this.formGroup.value.defaultDashboardSort === 'object'
                ? this.formGroup.value.defaultDashboardSort.value
                : this.formGroup.value.defaultDashboardSort;

        // Clear dashboard sort from localStorage if defaultDashboardSort changed
        if (defaultDashboardSort !== this.props.user.defaultDashboardSort) {
            const savedParams = localStorage.getItem('dashboardDataParams');
            if (savedParams) {
                try {
                    const params = JSON.parse(savedParams);
                    delete params.sort;
                    delete params.descending;
                    localStorage.setItem('dashboardDataParams', JSON.stringify(params));
                } catch (error) {
                    // If parsing fails, just clear it entirely
                    localStorage.removeItem('dashboardDataParams');
                }
            }
        }

        this.props.handleSubmit({
            ...this.props.user,
            ...this.formGroup.value,
            language: this.formGroup.value.language.value,
            email: this.props.user.email, // have to add back the email because disabling the input removes it
            userStandards: this.formGroup.value.standards.map((standard: Ioption) => standard.value),
            defaultDashboardSort
        });
    };

    setForm = (form: AbstractControl) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };

        // Populate form data immediately when form is mounted
        if (this.props.user) {
            this.populateFormWithUserData();
        }
    };

    render() {
        const { t } = this.props;

        return (
            <div className={this.props.colorButton}>
                <form onSubmit={this.handleSubmit} className="clearfix beacon-form user-form py-2">
                    <Col>
                        <Row>
                            <FormGenerator onMount={this.setForm} fieldConfig={this.fieldConfig} />
                        </Row>
                    </Col>

                    <Col xs={12} className="form-buttons text-right">
                        <Button
                            variant="link"
                            type="button"
                            className="pull-left left-side"
                            onClick={this.props.toggleModal}
                        >
                            {String(t('cancel'))}
                        </Button>
                        <Button variant={this.props.colorButton} type="submit" disabled={this.props.loading}>
                            {String(t('save'))}
                        </Button>
                    </Col>
                </form>
            </div>
        );
    }
}
export default UserProfileForm;
